import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import ValidatedInput from "../../components/ValidatedInput/ValidatedInput";
import Typography from "../../components/Typography/Typography";
import Button from "../../components/Button/Button";
import Loader from "../../components/Loader/Loader";
import Modal from "../../components/Modal/Modal";
import CompleteRegisterImg from "../../assets/loginImage.svg";
import styles from "./CompleteRegister.module.scss";
import codes from "../../utils/codes";

import { ReactComponent as ArrowLeft } from "../../assets/arrowLeft.svg";
import { StateContext } from "../../contexts/state.context";
import { loyaltyRegistrationCheckUser } from "../../utils/services";

const MAX_ID_LENGTH = 10;
const onlyDigits = (value) => (value || "").replace(/\D/g, "").slice(0, MAX_ID_LENGTH);

const GENDER_MAP = { M: "Hombre", F: "Mujer" };

// lpmData (Endpoint 1) -> formData que esperan PersonalData / LastSteps
const mapLpmDataToFormData = (lpmData, identifier, country) => {
	const isoBirth = (lpmData?.fechaDeNacimiento || "").split("-");
	const fechaNacimiento =
		isoBirth.length === 3
			? `${isoBirth[2]}/${isoBirth[1]}/${isoBirth[0]}`
			: undefined;

	return {
		fromPos: true,
		id: lpmData?.numeroDeIdentificacion || identifier,
		docType: "CC",
		nombres: lpmData?.firstName,
		apellidos: lpmData?.lastName,
		email: lpmData?.email,
		celphone: lpmData?.mobilePhone,
		addr: lpmData?.streetAddress,
		// Departamento y ciudad NO se precargan: el usuario los elige en
		// PersonalData (el select de ciudad depende del departamento elegido).
		country: lpmData?.country || country,
		gender: GENDER_MAP[lpmData?.gender] || lpmData?.gender,
		fechaNacimiento,
	};
};

const CompleteRegister = () => {
	const navigate = useNavigate();
	const { setFormData } = useContext(StateContext);

	const [id, setId] = useState();
	const [idError, setIdError] = useState(false);

	// null | "found" (E1) | "notFound" (E2) | "hasAccount" (E3)
	const [scenario, setScenario] = useState(null);
	const [pendingFormData, setPendingFormData] = useState(null);
	const [externalServiceModal, setExternalServiceModal] = useState(false);

	const [loader, setLoader] = useState(false);

	const handleBackClick = () => {
		navigate("/login");
	};

	const handleCancelClick = () => {
		navigate("/login");
	};

	const handleBtnDisabled = () => {
		if (id) return false;
		else return true;
	};

	const handleContinue = () => {
		if (!id) {
			setIdError(true);
			return;
		}

		const identifier = id.replaceAll(".", "");
		const country = "Colombia";

		setLoader(true);

		loyaltyRegistrationCheckUser({ formInfo: { identifier, country } })
			.then(({ code, message }) => {
				setLoader(false);

				if (
					code !== codes.JVB001.code ||
					message?.message !== "LOYALTY_CHECK_DONE"
				) {
					setExternalServiceModal(true);
					return;
				}

				const { userExists, lpmExists, lpmData } = message;

				// E3: ya tiene cuenta y contraseña -> Login / Olvidé mi contraseña
				if (userExists) {
					setScenario("hasAccount");
					return;
				}

				// E2: no encontrado -> Crear cuenta nueva / Volver a intentar
				if (!lpmExists || !lpmData) {
					setScenario("notFound");
					return;
				}

				// E1: cliente de tienda física, le falta la contraseña.
				// Se muestra el modal "¡Te encontramos, Amigo!" y al confirmar
				// se reutiliza el formulario del registro tradicional con los
				// datos precargados del Endpoint 1.
				setPendingFormData(mapLpmDataToFormData(lpmData, identifier, country));
				setScenario("found");
			})
			.catch(() => {
				setLoader(false);
				setExternalServiceModal(true);
			});
	};

	return (
		<div className={styles.completeRegisterContainer}>
			<div className={styles.completeRegisterTitleContainer}>
				<ArrowLeft
					className={styles.completeRegisterTitleArrow}
					onClick={handleBackClick}
				></ArrowLeft>

				<div className={styles.completeRegisterTitleTxt}>
					<Typography variant="h5">Completa tu registro</Typography>
				</div>
			</div>

			<div className={styles.completeRegisterCard}>
				<img
					src={CompleteRegisterImg}
					alt="complete-register-img"
					className={styles.completeRegisterCardImg}
				></img>

				<div className={styles.completeRegisterCardContent}>
					<Typography
						variant="h5"
						className={styles.completeRegisterCardTitle}
					>
						Completa tu registro
					</Typography>

					<Typography className={styles.completeRegisterCardTxt}>
						Si ya te registraste en nuestras{" "}
						<span style={{ fontWeight: "bold" }}>tiendas físicas</span>
						, ingresa tu número de identificación. Así podremos
						encontrar tu perfil de Amigo Juan Valdez, vincular tus
						beneficios y ayudarte a crear una contraseña para la App.
					</Typography>

					<ValidatedInput
						className={styles.completeRegisterTextInput}
						label="Número de identificación"
						placeholder="Escribe aquí número de identificación"
						type="text"
						inputMode="numeric"
						maxLength={MAX_ID_LENGTH}
						onChange={(v) => {
							setId(v);
							setIdError(false);
						}}
						error={idError}
						setError={setIdError}
						errorMessage="Campo inválido"
						formatter={onlyDigits}
						style={{ textAlign: "center" }}
					></ValidatedInput>

					<Typography
						variant="p3"
						color="gray"
						className={styles.completeRegisterHelperTxt}
					>
						Ingresa solo números, sin comas ni puntos.
					</Typography>

					<Button
						className={styles.completeRegisterBtn}
						onClick={handleContinue}
						disabled={handleBtnDisabled()}
					>
						Continuar
					</Button>
				</div>
			</div>

			<div onClick={handleCancelClick}>
				<Typography className={styles.completeRegisterCancel} variant="h4">
					Cancelar
				</Typography>
			</div>

			{scenario === "found" && (
				<Modal title="¡Te encontramos, Amigo!" isSuccess>
					<>
						<Typography className={styles.completeRegisterModalTxt}>
							Tu perfil ya está creado. Solo te falta completar tu
							registro y crear una contraseña segura para empezar a
							disfrutar de tus beneficios y acumular puntos desde la
							App.
						</Typography>

						<Button
							className={styles.completeRegisterModalBtn}
							onClick={() => {
								setFormData(pendingFormData);
								navigate("/register/personal-data");
							}}
						>
							Crear contraseña
						</Button>
					</>
				</Modal>
			)}

			{scenario === "notFound" && (
				<Modal
					title="Aún no eres Amigo Juan Valdez"
					onClose={() => setScenario(null)}
				>
					<>
						<Typography className={styles.completeRegisterModalTxt}>
							No encontramos este número de documento en nuestros
							registros. ¡Pero no te preocupes! Únete ahora a nuestro
							programa y empieza a disfrutar de beneficios
							exclusivos.
						</Typography>

						<Button
							className={styles.completeRegisterModalBtn}
							onClick={() => navigate("/register/validate-identity")}
						>
							Crear cuenta nueva
						</Button>

						<div onClick={() => setScenario(null)}>
							<Typography
								variant="p3"
								className={styles.completeRegisterModalBack}
							>
								Volver a intentar
							</Typography>
						</div>
					</>
				</Modal>
			)}

			{scenario === "hasAccount" && (
				<Modal title="¡Ya tienes una cuenta activa!">
					<>
						<Typography className={styles.completeRegisterModalTxt}>
							Tu registro ya está completo y tienes una contraseña
							creada. Puedes iniciar sesión ahora mismo. Si no la
							recuerdas, te ayudamos a crear una nueva de forma
							segura.
						</Typography>

						<Button
							className={styles.completeRegisterModalBtn}
							onClick={() => navigate("/login")}
						>
							Iniciar sesión
						</Button>

						<div onClick={() => navigate("/forgot-pws")}>
							<Typography
								variant="p3"
								className={styles.completeRegisterModalBack}
							>
								Olvidé mi contraseña
							</Typography>
						</div>
					</>
				</Modal>
			)}

			{externalServiceModal && (
				<Modal
					title="Estamos presentando una intermitencia"
					onClose={() => setExternalServiceModal(false)}
				>
					<>
						<Typography className={styles.completeRegisterModalTxt}>
							No pudimos validar tu información por una falla
							temporal en servicios externos. Por favor intenta
							nuevamente en unos minutos.
						</Typography>

						<Button
							className={styles.completeRegisterModalBtn}
							onClick={() => setExternalServiceModal(false)}
						>
							Cerrar
						</Button>
					</>
				</Modal>
			)}

			{loader && <Loader />}
		</div>
	);
};

export default CompleteRegister;
